import api from '@/utils/api';
import { UploadResponse } from '@/types/upload';

class UploadService {
  async uploadImage(file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/upload/images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  }

  async uploadDocument(file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/upload/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  }

  validateFile(file: File, fileType: 'image' | 'document', maxSizeMB: number = 5): { valid: boolean; error?: string } {
    // Check file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      return {
        valid: false,
        error: `File size must be less than ${maxSizeMB}MB`,
      };
    }

    // Check file type
    if (fileType === 'image') {
      if (!file.type.startsWith('image/')) {
        return {
          valid: false,
          error: 'Please upload an image file',
        };
      }
    } else if (fileType === 'document') {
      // Add document type validation if needed
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        return {
          valid: false,
          error: 'Please upload a valid document (PDF, DOC, DOCX)',
        };
      }
    }

    return { valid: true };
  }

  async uploadMxmDocument(
    file: File,
    title: string,
    description: string,
    category: string
  ): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('category', category);

    // new MXM endpoint (matches Swagger: /api/mxm/posts/documents)
    const response = await api.post('/mxm/posts/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  }

  async generateDocumentSummary(file: File): Promise<{ summary: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const res = await api.post('/mxm/posts/documents/summary', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return res.data;
  }

  async getAllDocuments(limit: number = 100, offset: number = 0, search: string = ''): Promise<{ data: UploadResponse[], total: number }> {
    const response = await api.get('/mxm/posts/all-upload', {
      params: { limit, offset, search }
    });
    return response.data;
  }
}

export const uploadService = new UploadService();

